import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import { AddressDTO } from 'artifacts/dtos';
import { isEmptyString } from 'utils/helper';
import { googleMapsApiKey } from 'env';
import { useGetGeoCodeByAddress } from 'hooks/useApi';

export function useGoogleMapGeoCode(address: string) {
  const [data, setData] = useState<AddressDTO>();
  const {
    mutateAsync: getGeoCodeByAddress,
    isPending: isLoading,
    isError: hasError,
  } = useGetGeoCodeByAddress();

  const getAddress = async () => {
    try {
      const _address = await getGeoCodeByAddress(address);
      setData(_address);
    } catch (error) { /* handled by mutation state */ }
  };

  useEffect(() => {
    if (!isEmptyString(address)) {
      getAddress();
    }
  }, [address]);

  return { isLoading, hasError, data };
}

export const useGetGooglePlaces = (
  input: string,
): UseQueryResult<Partial<google.maps.places.AutocompletePrediction>[], Error> => {
  const autocompleteSuggestionRef = useRef<
    typeof google.maps.places.AutocompleteSuggestion | null
  >(null);
  useEffect(() => {
    if (isNil(autocompleteSuggestionRef.current)
      && !isNil(window?.google?.maps?.places?.AutocompleteSuggestion)
    ) {
      autocompleteSuggestionRef.current = window.google.maps.places.AutocompleteSuggestion;
    }
  }, []);

  return useQuery<Partial<google.maps.places.AutocompletePrediction>[], Error>({
    queryKey: ['places', input],
    queryFn: async () => {
      const AutocompleteSuggestion = autocompleteSuggestionRef.current;
      if (isNil(AutocompleteSuggestion)) {
        console.error('Google Places library not loaded');
        throw new Error('Google Places library not loaded');
      }
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['us'],
      });
      return compact((suggestions ?? [])
        .map((suggestion) => {
          const prediction = suggestion.placePrediction;
          const matches = prediction?.mainText?.matches ?? [];
          // Normalize new API result into legacy AutocompletePrediction-like
          // shape used by Address forms and `formatPlace()`.
          return isNil(prediction) ? null : {
            description: prediction.text?.text ?? '',
            place_id: prediction.placeId,
            structured_formatting: {
              main_text: prediction.mainText?.text ?? '',
              secondary_text: prediction.secondaryText?.text ?? '',
              main_text_matched_substrings: matches.map((m) => ({
                offset: m.startOffset,
                length: m.endOffset - m.startOffset,
              })),
            },
          } as Partial<google.maps.places.AutocompletePrediction>;
        }));
    },
    enabled: !isEmptyString(input),
  });
};

export function useGoogleMaps() {
  useEffect(() => {
    if (isEmptyString(googleMapsApiKey)) {
      console.error('Missing Google Maps API key.');

      return undefined;
    }

    if (isNil(window?.google?.maps) && isNil(document.getElementById('google-maps-script'))) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return undefined;
  }, [googleMapsApiKey]);
}
